import { Injectable, inject } from '@angular/core';
import { Observable } from 'rxjs';
import { environment } from '../../../../../../environments/environment';
import { AiChatRequest, AiChatStreamEvent } from '../models/ai-assistant.models';
import { AiAssistantSessionService } from './ai-assistant-session.service';
import { AiAssistantStreamParserService } from './ai-assistant-stream-parser.service';

@Injectable({ providedIn: 'root' })
export class AiAssistantApiService {
  private readonly sessionService = inject(AiAssistantSessionService);
  private readonly parser = inject(AiAssistantStreamParserService);

  sendMessage(message: string): Observable<AiChatStreamEvent> {
    return new Observable<AiChatStreamEvent>((subscriber) => {
      const controller = new AbortController();
      const request = this.createRequest(message);

      void this.streamRequest(request, controller, subscriber);

      return () => {
        controller.abort();
      };
    });
  }

  private createRequest(message: string): AiChatRequest {
    return {
      sessionId: this.sessionService.getSessionId(),
      investigationId: this.sessionService.getInvestigationId(),
      message,
      mode: 'auto',
      stream: true,
    };
  }

  private async streamRequest(
    request: AiChatRequest,
    controller: AbortController,
    subscriber: {
      next: (value: AiChatStreamEvent) => void;
      error: (error: unknown) => void;
      complete: () => void;
    },
  ): Promise<void> {
    try {
      this.parser.reset();
      const response = await fetch(environment.ai_chat_endpoint, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Accept: 'text/event-stream',
        },
        body: JSON.stringify(request),
        signal: controller.signal,
      });

      if (!response.ok) {
        throw new Error(`AI chat request failed: ${response.status} ${response.statusText}`);
      }

      if (!response.body) {
        throw new Error('AI chat response did not include a readable stream.');
      }

      const reader = response.body.getReader();
      const decoder = new TextDecoder();

      while (true) {
        const result = await reader.read();

        if (result.done) {
          break;
        }

        const chunk = decoder.decode(result.value, { stream: true });
        for (const event of this.parser.push(chunk)) {
          subscriber.next(event);
        }
      }

      for (const event of this.parser.flush()) {
        subscriber.next(event);
      }

      subscriber.complete();
    } catch (error) {
      if (!controller.signal.aborted) {
        subscriber.error(error);
      }
    }
  }
}
